import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Printer, ArrowLeft } from "lucide-react";

const BASE_URL = "http://nissi-india.com";

function getQuery() {
  const params = new URLSearchParams(window.location.search);
  return {
    finYearId: params.get("finYearId") || "",
    branchId: params.get("branchId") ?? "",
    fromYear: params.get("fromYear") || "",
    toYear: params.get("toYear") || "",
  };
}

const ResignedStaffBonusListPrint = () => {
  const q = useMemo(() => getQuery(), []);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [didAutoPrint, setDidAutoPrint] = useState(false);

  const monthKeys = useMemo(
    () => [
      "October",
      "November",
      "December",
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
    ],
    []
  );

  const show0 = (v) => (v === null || v === undefined || v === "" ? "0" : String(v));
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const totals = useMemo(() => {
    const t = {
      October: 0,
      November: 0,
      December: 0,
      January: 0,
      February: 0,
      March: 0,
      April: 0,
      May: 0,
      June: 0,
      July: 0,
      August: 0,
      September: 0,
      Total: 0,
      Batta: 0,
      "Batta Bonus": 0,
      "Total Bonus": 0,
    };

    (rows || []).forEach((r) => {
      monthKeys.forEach((k) => (t[k] += num(r?.[k])));
      t.Total += num(r?.Total);
      t.Batta += num(r?.Batta);
      t["Batta Bonus"] += num(r?.["Batta Bonus"]);
      t["Total Bonus"] += num(r?.["Total Bonus"]);
    });

    return t;
  }, [rows, monthKeys]);

  const fetchAll = async () => {
    if (!String(q.finYearId).trim()) return toast.warn("finYearId missing in URL");
    if (!q.fromYear || !q.toYear) return toast.warn("fromYear/toYear missing in URL");

    const branchNormalized = String(q.branchId).trim() === "Select" ? "" : String(q.branchId).trim();

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/bonus-resigned-staff/print-data`, {
        params: {
          finYearId: Number(q.finYearId),
          branchId: branchNormalized,
          fromYear: q.fromYear,
          toYear: q.toYear,
        },
      });

      if (res?.data?.success) setRows(res.data.rows || []);
      else toast.error(res?.data?.message || "Failed to load print data");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto print after paint
  useEffect(() => {
    if (didAutoPrint) return;
    if (loading) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        setDidAutoPrint(true);
      });
    });
  }, [loading, didAutoPrint]);

  return (
    <div className="min-h-screen bg-white text-black">
      <ToastContainer position="top-right" autoClose={2200} />

      {/* Controls (hidden on print) */}
      <div className="p-4 border-b flex items-center justify-between print:hidden">
        <button
          onClick={() => window.close()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border font-bold hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4" /> Close
        </button>

        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700"
          disabled={loading}
        >
          <Printer className="w-4 h-4" />
          Print
        </button>
      </div>

      {/* ✅ ONLY THIS AREA WILL PRINT */}
      <div id="print-area" className="p-6">
        <div className="border-2 border-black">
          <div className="p-3 border-b border-black">
            <div className="grid grid-cols-12 gap-2 items-start">
              <div className="col-span-2">
                <div className="border border-black h-14 flex items-center justify-center text-[10px] font-bold">
                  LOGO
                </div>
              </div>

              <div className="col-span-8 text-center leading-tight">
                <div className="font-extrabold text-sm">Nissi Infotech</div>
                <div className="text-[10px]">3/42-12, Nagappa colony , kavundampalayam</div>
                <div className="text-[10px]">CBE</div>
                <div className="text-[10px]">641030</div>
                <div className="text-[10px]">9003744644</div>
                <div className="text-[10px]">Tamil Nadu</div>
                <div className="text-[10px]">-</div>

                <div className="text-[10px] mt-1">Nissi</div>
                <div className="text-[10px]">.</div>

                <div className="text-[10px] mt-1">Phone No :</div>
                <div className="text-[10px]">-</div>

                <div className="text-[10px] mt-1">India</div>
                <div className="text-[10px] font-bold">CIVIL CONSTRUCTION</div>
                <div className="text-[10px]">.</div>
                <div className="text-[10px]">.</div>

                <div className="text-[10px]">infraconsoft-implementation@nissiinfotech.in</div>
              </div>

              <div className="col-span-2" />
            </div>
          </div>

          <div className="text-center font-bold text-[11px] py-2 border-b border-black">
            RESIGNED STAFF&apos;S BONUS MASTER LIST FOR THE YEAR OF {q.fromYear} TO {q.toYear}
          </div>

          {/* ✅ remove overflow on print */}
          <div className="p-2 overflow-auto print-no-overflow">
            <table className="w-full border-collapse text-[10px]">
              <thead>
                <tr>
                  {[
                    "S.No","Code","Employee Name","Designation","DOJ","A/C No.",
                    "Oct","Nov","Dec","Jan","Feb","Mar","Apr","May","Jun","July","Aug","Sep",
                    "Total","Bonus %","Batta","Batta Bonus","Total Bonus","Location",
                  ].map((h) => (
                    <th key={h} className="border border-black px-1 py-1 font-bold text-center whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={24} className="border border-black px-2 py-8 text-center">
                      {loading ? "Loading..." : "No Records Found"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={idx}>
                      <td className="border border-black px-1 py-1 text-center">{idx + 1}</td>
                      <td className="border border-black px-1 py-1 text-center">{r?.EmployeeCode ?? ""}</td>
                      <td className="border border-black px-1 py-1">{r?.["Employee Name"] ?? ""}</td>
                      <td className="border border-black px-1 py-1 text-center">{r?.Designation ?? ""}</td>
                      <td className="border border-black px-1 py-1 text-center">{r?.DOJ ?? ""}</td>
                      <td className="border border-black px-1 py-1 text-center">{r?.["A/C No."] ?? ""}</td>

                      {monthKeys.map((m) => (
                        <td key={m} className="border border-black px-1 py-1 text-right">
                          {show0(r?.[m])}
                        </td>
                      ))}

                      <td className="border border-black px-1 py-1 text-right">{show0(r?.Total)}</td>
                      <td className="border border-black px-1 py-1 text-right">{show0(r?.fld_bonuspercentage)}</td>
                      <td className="border border-black px-1 py-1 text-right">{show0(r?.Batta)}</td>
                      <td className="border border-black px-1 py-1 text-right">{show0(r?.["Batta Bonus"])}</td>
                      <td className="border border-black px-1 py-1 text-right">{show0(r?.["Total Bonus"])}</td>
                      <td className="border border-black px-1 py-1 text-center">{r?.Location ?? ""}</td>
                    </tr>
                  ))
                )}
              </tbody>

              {rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td className="border border-black px-1 py-1 font-bold text-center" colSpan={6}>
                      Total Bonus(Rs)
                    </td>

                    {monthKeys.map((m) => (
                      <td key={m} className="border border-black px-1 py-1 font-bold text-right">
                        {totals[m].toFixed(2)}
                      </td>
                    ))}

                    <td className="border border-black px-1 py-1 font-bold text-right">
                      {totals.Total.toFixed(2)}
                    </td>

                    <td className="border border-black px-1 py-1" />

                    <td className="border border-black px-1 py-1 font-bold text-right">
                      {totals.Batta.toFixed(2)}
                    </td>
                    <td className="border border-black px-1 py-1 font-bold text-right">
                      {totals["Batta Bonus"].toFixed(2)}
                    </td>
                    <td className="border border-black px-1 py-1 font-bold text-right">
                      {totals["Total Bonus"].toFixed(2)}
                    </td>
                    <td className="border border-black px-1 py-1" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* ✅ PRINT FIX CSS */}
        <style>{`
          #print-area { background: white; }

          @media print {
            @page { size: A4 landscape; margin: 8mm; }

            body * { visibility: hidden !important; }
            #print-area, #print-area * { visibility: visible !important; }

            #print-area {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              overflow: visible !important;
            }

            .print-no-overflow {
              overflow: visible !important;
              max-height: none !important;
              height: auto !important;
            }

            html, body {
              background: #fff !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ResignedStaffBonusListPrint;
